import { useState } from 'react';
import { useSession } from '../App';
import { Card, ConfirmDialog, ErrorNotice, Notice, PageHeader } from '../components/ui';

export function SecurityPage() {
  return (
    <>
      <PageHeader
        eyebrow="Security & privacy"
        title="Security and your data"
        description="Sign out of devices you do not recognise, take a copy of your data, or close your account."
      />
      <SignOutEverywhere />
      <ExportData />
      <DeleteAccount />
    </>
  );
}

function SignOutEverywhere() {
  const { client, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ complete: boolean; revokedDevices: number } | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setResult(await client.signOutEverywhere());
    } catch (nextError) {
      setError(nextError);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <Card title="Sign out everywhere">
      <p className="muted">
        Removes every device from your account and ends every DeskTouch session, including this one. Use it if a device
        was lost or you suspect someone else has access.
      </p>
      <ErrorNotice error={error} />
      {result ? (
        <Notice tone={result.complete ? 'success' : 'warning'}>
          <p>
            <strong>
              {result.revokedDevices} device{result.revokedDevices === 1 ? '' : 's'} removed, and every access token issued
              before now is refused.
            </strong>
          </p>
          {!result.complete && (
            <p>
              Sign-in sessions at the identity provider could not be ended automatically, so a device that still holds one
              could sign in again. If you think your password is known to someone, change it as well.
            </p>
          )}
          <button type="button" className="primary" onClick={() => void signOut()}>
            Sign in again
          </button>
        </Notice>
      ) : (
        <button type="button" className="danger" onClick={() => setOpen(true)}>
          Sign out everywhere
        </button>
      )}
      <ConfirmDialog
        open={open}
        title="Sign out everywhere?"
        confirmLabel="Sign out everywhere"
        tone="danger"
        busy={busy}
        onCancel={() => setOpen(false)}
        onConfirm={() => void run()}
      >
        <p>All your devices will need to sign in again, and so will you, here.</p>
      </ConfirmDialog>
    </Card>
  );
}

function ExportData() {
  const { client } = useSession();
  const [busy, setBusy] = useState<'json' | 'zip' | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function download(format: 'json' | 'zip') {
    setBusy(format);
    setError(null);
    try {
      const blob = await client.exportData(format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `desktouch-account-export-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (nextError) {
      setError(nextError);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title="Download your data">
      <p className="muted">
        A copy of everything DeskTouch holds about you: profile, devices, plan and payments, and your synced layouts and
        settings with their history.
      </p>
      <ErrorNotice error={error} />
      <div className="button-row">
        <button type="button" onClick={() => void download('zip')} disabled={busy !== null}>
          {busy === 'zip' ? 'Preparing…' : 'Download ZIP'}
        </button>
        <button type="button" className="ghost" onClick={() => void download('json')} disabled={busy !== null}>
          {busy === 'json' ? 'Preparing…' : 'Download JSON'}
        </button>
      </div>
    </Card>
  );
}

function DeleteAccount() {
  const { client, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [deleted, setDeleted] = useState(false);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await client.deleteAccount(reason.trim() || undefined);
      setDeleted(true);
      setOpen(false);
    } catch (nextError) {
      setError(nextError);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  if (deleted) {
    return (
      <Card title="Account deleted" className="danger-zone">
        <p>Your account has been deleted. Payment records are kept only as long as the law requires.</p>
        <button type="button" className="primary" onClick={() => void signOut()}>
          Finish
        </button>
      </Card>
    );
  }

  return (
    <Card title="Delete account" className="danger-zone">
      <p className="muted">
        Permanently deletes your profile, devices and synced data, and signs out every device. This cannot be undone. A paid
        period that is still running is not refunded. Invoices and payment records are kept as the law requires.
      </p>
      <ErrorNotice error={error} />
      <button type="button" className="danger" onClick={() => setOpen(true)}>
        Delete my account
      </button>
      <ConfirmDialog
        open={open}
        title="Delete your DeskTouch account?"
        confirmLabel="Delete forever"
        tone="danger"
        busy={busy}
        confirmDisabled={confirmation !== 'DELETE'}
        onCancel={() => {
          setOpen(false);
          setConfirmation('');
        }}
        onConfirm={() => void run()}
      >
        <p>
          Consider downloading your data first. Type <strong>DELETE</strong> to confirm.
        </p>
        <label>
          <span>Confirmation</span>
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" />
        </label>
        <label>
          <span>Why are you leaving? (optional)</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={3} />
        </label>
      </ConfirmDialog>
    </Card>
  );
}
